import { useEffect, useRef } from "react";
import { useFetcher } from "react-router";

import {
  type DispatcherPlpResponse,
  isPlpPaginationError,
  unwrapPlpPayload,
} from "~/routes/dispatcher-plp-payload";

/**
 * Shared state machine for the PLP's Load More and Start From The
 * Beginning fetchers.
 *
 * Both flows do the same dance:
 *   1. At click time, snapshot a `filterSignature` (pathname + non-
 *      pagination search params) and the `targetUrl` we want to push
 *      onto history when the response lands.
 *   2. Fire `fetcher.load(fetchUrl)` against the PLP loader.
 *   3. When the response lands, compare the current signature to the
 *      snapshot. If a filter / sort / query click or a cross-category
 *      navigation happened mid-flight, drop the response. Otherwise
 *      resolve the deferred hits Promise and call `onResolved`, which
 *      decides how to merge (append vs prepend) and what to do with
 *      `targetUrl`.
 *
 * Centralising this here means:
 *   - `CategoryGridBody` shrinks to two `usePaginationFetcher` calls +
 *     two click handlers.
 *   - The stale-fetch guard lives in one place, so any change to how
 *     `filterSignature` is computed propagates to both flows.
 */

type FetchSnapshot = {
  signature: string;
  targetUrl: string;
};

export type PaginationFetchResult<TData> = {
  /** Resolved hits-payload (already unwrapped from the dispatcher envelope). */
  resolved: TData | undefined;
  /** URL captured at click time — pass to `navigate()` so closure drift can't change it. */
  targetUrl: string;
  /**
   * SFCC-11462: `true` when the loader returned an error-as-data sentinel for
   * this paginated request (CIO failed after retries). The caller skips the
   * merge and surfaces a non-blocking notification — the accumulated grid
   * stays mounted, no route ErrorBoundary fires.
   */
  error?: boolean;
};

export function usePaginationFetcher<TData>({
  key,
  filterSignature,
  onResolved,
}: {
  /** RR7 fetcher key (e.g. `plp-load-more`). */
  key: string;
  /** Current pathname + non-pagination search params — see `dispatcher-should-revalidate.ts`. */
  filterSignature: string;
  /** Called when the response lands AND the signature still matches. */
  onResolved: (result: PaginationFetchResult<TData>) => void;
}): {
  trigger: (fetchUrl: string, targetUrl: string) => void;
  isLoading: boolean;
} {
  const fetcher = useFetcher<DispatcherPlpResponse<TData>>({ key });
  const snapshotRef = useRef<FetchSnapshot | null>(null);
  // Belt-and-suspenders on top of the effect's dep array — React
  // StrictMode double-invokes effects in dev. Ref-equality short-circuit
  // collapses the second invocation into a no-op.
  const lastDataRef = useRef<unknown>(null);

  useEffect(() => {
    if (fetcher.state !== "idle" || !fetcher.data) return;
    if (fetcher.data === lastDataRef.current) return;
    lastDataRef.current = fetcher.data;

    const snap = snapshotRef.current;
    snapshotRef.current = null;
    if (snap === null) return;
    // Filter / sort / query click OR cross-category navigation landed
    // mid-flight — drop the stale response.
    if (snap.signature !== filterSignature) return;

    const payload = unwrapPlpPayload(fetcher.data);
    const dataField = payload.data;
    Promise.resolve(dataField).then(resolved => {
      // SFCC-11462: the loader returned an error-as-data sentinel for this
      // paginated request. Skip the merge and flag the error so the caller can
      // toast — the existing grid stays mounted (RR7 keeps the fetcher in
      // `idle` + `data`, so no throw and no route ErrorBoundary).
      if (isPlpPaginationError(resolved)) {
        onResolved({
          resolved: undefined,
          targetUrl: snap.targetUrl,
          error: true,
        });
        return;
      }
      onResolved({ resolved, targetUrl: snap.targetUrl });
    });
  }, [fetcher.state, fetcher.data, filterSignature, onResolved]);

  const trigger = (fetchUrl: string, targetUrl: string) => {
    if (fetcher.state !== "idle") return;
    snapshotRef.current = { signature: filterSignature, targetUrl };
    fetcher.load(fetchUrl);
  };

  return {
    trigger,
    isLoading: fetcher.state !== "idle",
  };
}
